import { v4 as uuidv4 } from 'uuid';
import { defaultLogger as logger } from './logger';
import type { IDependencyTelemetry } from '@microsoft/applicationinsights-web';

export function trackedFetch(
  tenantId: string,
  input: RequestInfo,
  init: RequestInit = {}
) {
  // Get the URL string from either a string input or Request object
  const url = typeof input === 'string' ? input : input.url;

  // Parse the URL to get components
  const parsedUrl = new URL(url);

  // Get start time
  const startTime = Date.now();

  // Start the dependency tracking
  const dependency: IDependencyTelemetry = {
    id: uuidv4(),
    name: `${init.method || 'GET'} ${parsedUrl.origin}${parsedUrl.pathname}`,
    startTime: new Date(),
    target: url,
    data: url,
    duration: 0,
    responseCode: 0,
    success: false,
    type: 'Fetch',
    properties: {
      HttpMethod: init.method || 'GET',
      tenant: tenantId,
    },
  };

  //logger.trackDependency(dependency);

  // Execute the fetch
  return fetch(input, init)
    .then((response) => {
      const duration = Date.now() - startTime;

      // This block will be reached if the server sends proper CORS headers,
      // even for error responses like 429.
      if (!response.ok) {
        // Create a custom error that includes the status
        const httpError: any = new Error(`HTTP error ${response.status}: ${response.statusText}`);
        httpError.status = response.status;
        httpError.statusText = response.statusText;
        httpError.response = response; // Attach the original response for more context
        
        // For 429 errors, try to extract the retry-after header
        if (response.status === 429) {
          const retryAfter = response.headers.get('retry-after');
          if (retryAfter) {
            httpError.retryAfter = retryAfter;
          }
        }
        
        // Log the dependency failure before throwing
        logger.trackDependency({
          id: dependency.id,
          name: dependency.name,
          startTime: dependency.startTime,
          duration: duration,
          success: false, // response.ok is false
          responseCode: response.status,
          type: dependency.type,
          target: dependency.target,
          data: dependency.data, // Or consider httpError.message for more specific data on error
          properties: dependency.properties,
        });
        
        throw httpError; // Throw the custom HTTP error
      }

      // If response is OK, then log success
      logger.trackDependency({
        id: dependency.id,
        name: dependency.name,
        startTime: dependency.startTime,
        duration: duration,
        success: true, // response.ok is true here
        responseCode: response.status,
        type: dependency.type,
        target: dependency.target,
        data: dependency.data,
        properties: dependency.properties,
      });

      return response;
    })
    .catch((error) => {
      const duration = Date.now() - startTime;

      // This catch block handles:
      // 1. Network errors (e.g., server down, DNS issues).
      // 2. CORS errors (like the TypeError you're currently seeing due to missing server CORS headers).
      // 3. Errors thrown from the .then() block (e.g., our custom httpError).

      // If 'error.status' is undefined, it's likely a network/CORS error (TypeError)
      // or an unexpected client-side error before a response object was available.
      // Our custom httpError (thrown from .then()) will have 'error.status' defined,
      // and its telemetry was already logged before it was thrown.
      if (typeof error.status === 'undefined') {
        logger.trackDependency({
          id: dependency.id,
          name: dependency.name,
          startTime: dependency.startTime,
          duration: duration,
          success: false,
          responseCode: 0, // For TypeError or network errors, status code isn't available to JS.
          type: dependency.type,
          target: dependency.target,
          data: String(error), // Capture the error message (e.g., "TypeError: Failed to fetch").
          properties: {
            ...dependency.properties,
            error: String(error),
            errorType: error.name || (error.constructor && error.constructor.name) || 'UnknownError',
            // Consider if init.body should be logged, due to size or sensitivity.
            // body: init.body ? String(init.body) : undefined 
          },
        });
      }
      // If error.status is defined, it's our custom httpError, and telemetry was handled in .then().

      throw error; // Re-throw the error to maintain the promise chain
    });
}
